import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, Plus, X, CalendarDays, Columns3 } from "lucide-react";
import Segment from "../components/Segment";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import ActivityItem from "../components/ActivityItem";
import AktivitaDetail from "../components/AktivitaDetail";
import NovaAktivita from "../components/NovaAktivita";
import { Button } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import { canEditAktivita } from "../lib/nav";
import { barvaOsoby } from "../lib/osoby";
import { useJeMobil } from "../lib/mobil";
import KalendarMobil from "../components/KalendarMobil";
import { useObnovaPriNavratu } from "../lib/obnova";
import api from "../lib/api";
import { useUrlStav } from "../lib/navigace";
import OsobaKolecko from "../components/OsobaKolecko";
import { KanbanSloupce, KanbanMobil, useKanban } from "../components/UkolyKanban";

const MONTHS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec",
  "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
const DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];

// Barvy událostí dle závazné předlohy 3d: blok = STAV (hotovo/otevřené/po
// termínu), osoba je jen tečka — dva významy nesmí sdílet jednu barvu.
const STAV_BLOK = {
  HOTOVA: "bg-[#eaf6f1] text-[#126b53]",
  OTEVRENA: "bg-[#eef4fd] text-[#2c5c9e]",
  PO_TERMINU: "bg-[#fdeae7] text-[#b02f22]",
};
const LEGENDA = [
  ["Hotovo", "#17876a"], ["Otevřené", "#2c6ecb"], ["Po termínu", "#d93a2b"],
];
const OBLASTI = [[null, "Vše"], ["SERVIS", "Servis"], ["OBCHOD", "Obchod"]];
const VIEWS = [["MESIC", "Měsíc"], ["TYDEN", "Týden"], ["SEZNAM", "Seznam"]];

function startOfMonthGrid(d) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const off = (first.getDay() + 6) % 7;
  const s = new Date(first);
  s.setDate(s.getDate() - off);
  s.setHours(0, 0, 0, 0);
  return s;
}
function startOfWeek(d) {
  const s = new Date(d);
  s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  s.setHours(0, 0, 0, 0);
  return s;
}
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const cas = (iso) => new Date(iso).toLocaleTimeString("cs-CZ", { hour: "numeric", minute: "2-digit" });
const stavKey = (a) => (a.po_terminu ? "PO_TERMINU" : a.stav === "HOTOVA" ? "HOTOVA" : "OTEVRENA");

/** Úkoly: dva náhledy jako Seznam | Tabule u obchodních případů — Kalendář a Kanban. */
function PrepinacRezimu({ rezim, setRezim, mobil }) {
  return (
    <Segment className={mobil ? "filtr-pas w-full grid grid-cols-2" : "filtr-pas"} data-testid="ukoly-rezim">
      {[["kalendar", "Kalendář", CalendarDays], ["kanban", "Kanban", Columns3]].map(([v, label, Ikona]) => (
        <button key={v} type="button" data-on={rezim === v} onClick={() => setRezim(v)} data-testid={`ukoly-rezim-${v}`}
          className={`filtr-seg gap-1.5 ${mobil ? "justify-center" : ""} ${rezim === v ? "text-white" : "text-sec hover:text-ink"}`}>
          <Ikona size={14} />{label}
        </button>
      ))}
    </Segment>
  );
}

export default function Calendar() {
  const mobil = useJeMobil();
  const [rezim, setRezim] = useUrlStav("rezim", "kalendar");
  // Telefon má vlastní rozložení (handoff 14c); desktop zůstává.
  if (mobil) {
    return (
      <div className="flex flex-col gap-3">
        <PrepinacRezimu rezim={rezim} setRezim={setRezim} mobil />
        {rezim === "kanban" ? <KanbanTelefon /> : <KalendarMobil />}
      </div>
    );
  }
  return <KalendarDesktop rezim={rezim} prepinac={<PrepinacRezimu rezim={rezim} setRezim={setRezim} />} />;
}

/** Kanban na telefonu: moje úkoly, nebo celé firmy. */
function KanbanTelefon() {
  const { user } = useAuth();
  const [jenMoje, setJenMoje] = useUrlStav("moje", "1");
  const [sloupec, setSloupec] = useUrlStav("sloupec", "K_RESENI");
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [novy, setNovy] = useState(false);
  const osoby = React.useMemo(() => (jenMoje === "1" && user ? [user.id] : []), [jenMoje, user]);
  const k = useKanban({ osoby });
  const cip = (on) => `tap h-[38px] pill border px-[13px] t-meta font-600 ${on ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong"}`;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => setJenMoje("1")} className={cip(jenMoje === "1")} data-testid="kanban-moje">Moje</button>
        <button type="button" onClick={() => setJenMoje("0")} className={cip(jenMoje !== "1")} data-testid="kanban-firma">Celá firma</button>
        <button type="button" onClick={() => setNovy(true)} data-testid="kanban-novy"
          className="tap ml-auto h-[38px] pill bg-accent text-white px-3.5 t-meta font-700 inline-flex items-center gap-1"><Plus size={15} /> Úkol</button>
      </div>
      <KanbanMobil k={k} sloupec={sloupec} setSloupec={setSloupec} onOtevri={setDetailId} />
      {novy && <NovaAktivita defaultTyp="Úkol" onClose={() => setNovy(false)} onSaved={k.load} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(a) => { setDetailId(null); setEditAct(a); }} onChanged={k.load} />
      )}
      {editAct && <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)} onSaved={() => { setEditAct(null); k.load(); }} />}
    </div>
  );
}

function KalendarDesktop({ rezim, prepinac }) {
  const kanban = rezim === "kanban";
  const { user } = useAuth();
  const [detailId, setDetailId] = useState(null);
  const [editAct, setEditAct] = useState(null);
  const [cursor, setCursor] = useState(() => new Date());
  const [acts, setActs] = useState([]);
  const [selected, setSelected] = useState(() => new Date());
  const [newAct, setNewAct] = useState(false);

  const [view, setView] = useUrlStav("pohled", "MESIC");
  const [oblast, setOblast] = useUrlStav("oblast");
  // [] = firemní kalendář (všichni vč. subdodavatelů); jinak vybraní lidé.
  const [osoby, setOsoby] = useState([]);
  const [members, setMembers] = useState([]);
  const [poTerminu, setPoTerminu] = useState([]);
  // Seznam jen událostí po termínu (z upozornění nad kalendářem nebo z Domů).
  // V adrese, ať funguje odkaz z Domů i tlačítko zpět prohlížeče.
  const [jen, setJen] = useUrlStav("jen", "");
  const jenPoTerminu = jen === "po-terminu";
  const pohledPred = useRef("MESIC");
  const prepniPoTerminu = () => {
    if (jenPoTerminu) { setJen(""); setView(pohledPred.current); return; }
    pohledPred.current = view === "SEZNAM" ? "SEZNAM" : view;
    setView("SEZNAM"); setJen("po-terminu");
  };

  useEffect(() => {
    api.get("/api/team")
      .then(({ data }) => setMembers((data.members || []).filter((m) => m.active !== false)))
      .catch(() => setMembers([]));
  }, []);

  // Rozsah dat podle náhledu: měsíční mřížka 42 dní, týden 7.
  const range = useMemo(() => {
    if (view === "TYDEN") {
      const s = startOfWeek(cursor);
      const e = new Date(s); e.setDate(e.getDate() + 7);
      return { od: s, do: e };
    }
    const s = startOfMonthGrid(cursor);
    const e = new Date(s); e.setDate(e.getDate() + 42);
    return { od: s, do: e };
  }, [cursor, view]);

  // Po termínu se počítá globálně, ne jen v zobrazeném měsíci — a načítá se
  // spolu s kalendářem, ať odškrtnutá událost zmizí i ze seznamu po termínu.
  const load = useCallback(async () => {
    const [{ data }, p] = await Promise.all([
      api.get("/api/aktivity", { params: { od: range.od.toISOString(), do: range.do.toISOString(), limit: 500 } }),
      api.get("/api/aktivity", { params: { stav: "OTEVRENA", po_terminu: true, limit: 200 } }).catch(() => null),
    ]);
    setActs(data.items || []);
    if (p) setPoTerminu(p.data.items || []);
  }, [range]);
  useEffect(() => { load(); }, [load]);
  useObnovaPriNavratu(load);

  const k = useKanban({ oblast: kanban ? oblast : null, osoby: kanban ? osoby : null });
  const kOtevrene = k.d ? k.d.k_reseni.length + k.d.rozpracovano.length : 0;
  const kPoTerminu = k.d ? [...k.d.k_reseni, ...k.d.rozpracovano].filter((a) => a.po_terminu).length : 0;
  const nacist = () => { load(); if (kanban) k.load(); };

  // filtry se skládají: oblast × osoby (prázdné osoby = všichni)
  const visible = useMemo(() => acts.filter((a) =>
    (!oblast || a.oblast === oblast) &&
    (!osoby.length || (a.odpovedne_osoby || []).some((u) => osoby.includes(u)))
  ), [acts, oblast, osoby]);

  const byDay = (d) => visible.filter((a) => sameDay(new Date(a.date_start), d));
  const toggle = async (a) => { await api.post(`/api/aktivity/${a.id}/hotovo`); load(); };
  const posun = (smer) => {
    if (view === "TYDEN") {
      const d = new Date(cursor); d.setDate(d.getDate() + smer * 7); setCursor(d);
    } else {
      setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + smer, 1));
    }
  };
  const toggleOsoba = (id) =>
    setOsoby((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));

  const days = useMemo(() => Array.from(
    { length: view === "TYDEN" ? 7 : 42 },
    (_, i) => { const d = new Date(range.od); d.setDate(d.getDate() + i); return d; }
  ), [range, view]);

  const dayActs = byDay(selected).sort((x, y) => new Date(x.date_start) - new Date(y.date_start));
  const nejstarsi = poTerminu.length
    ? [...poTerminu].sort((x, y) => new Date(x.date_end) - new Date(y.date_end))[0] : null;

  const EventBlok = ({ a, plny }) => (
    <button onClick={(e) => { e.stopPropagation(); setDetailId(a.id); }}
      data-testid={`cal-event-${a.id}`}
      className={`w-full text-left rounded-[7px] px-1.5 py-1 text-[10.5px] font-600 truncate ${STAV_BLOK[stavKey(a)]}`}>
      {/* Tečka = osoba. Ukazuje se jen ve firemním pohledu — když filtruju
          na jednoho člověka, je nadbytečná. */}
      {!osoby.length && (a.odpovedne_osoby || []).slice(0, 1).map((u) => (
        <span key={u} className="inline-block h-1.5 w-1.5 rounded-full mr-1 align-middle"
          style={{ background: barvaOsoby(u) }} />
      ))}
      {plny && <span className="amount mr-1">{cas(a.date_start)}</span>}
      {a.nazev}
    </button>
  );

  return (
    <div data-testid="calendar-page">
      <PageHeader eyebrow="Úkoly" title={kanban ? "Kanban" : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
        subtitle={kanban
          ? (k.d ? `${kOtevrene} otevřených · ${k.d.rozpracovano.length} rozpracovaných${kPoTerminu ? ` · ${kPoTerminu} po termínu` : ""}` : " ")
          : `${visible.length} událostí${poTerminu.length ? ` · ${poTerminu.length} po termínu` : ""}`}
        actions={kanban ? (
          <Button onClick={() => setNewAct(true)} data-testid="kanban-novy"><Plus size={16} /> Nový úkol</Button>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => posun(-1)} className="btn-ghost h-9 w-9 px-0 justify-center" data-testid="cal-prev"><ChevronLeft size={17} /></button>
            <button onClick={() => { setCursor(new Date()); setSelected(new Date()); }}
              className="btn-ghost h-9" data-testid="cal-today">Dnes</button>
            <button onClick={() => posun(1)} className="btn-ghost h-9 w-9 px-0 justify-center" data-testid="cal-next"><ChevronRight size={17} /></button>
            {/* Na pozadí stránky bílý podklad s rámečkem — šedý splýval se stránkou. */}
            <Segment className="filtr-pas" data-testid="cal-views">
              {VIEWS.map(([v, label]) => (
                <button key={v} data-on={view === v} onClick={() => { setView(v); setJen(""); }}
                  data-testid={`cal-view-${v.toLowerCase()}`}
                  className={`filtr-seg ${view === v ? "text-white" : "text-sec hover:text-ink"}`}>
                  {label}
                </button>
              ))}
            </Segment>
            <Button onClick={() => setNewAct(true)} data-testid="cal-new"><Plus size={16} /> Nová aktivita</Button>
          </div>
        )} />

      {/* filtry: oblast + lidé; legenda vpravo (závazná předloha 3d) */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        {prepinac}
        <span className="w-px h-5 bg-line-strong mx-1" />
        {OBLASTI.map(([v, label]) => (
          <button key={label} onClick={() => setOblast(v)} data-testid={`cal-oblast-${label.toLowerCase()}`}
            className={`filtr ${
              oblast === v ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
            {label}
          </button>
        ))}
        <span className="w-px h-5 bg-line-strong mx-1" />
        <button onClick={() => setOsoby([])} data-testid="cal-osoby-vse"
          className={`filtr ${
            !osoby.length ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
          Celá firma
        </button>
        {members.map((m) => (
          <button key={m.id} onClick={() => toggleOsoba(m.id)} title={m.name}
            data-testid={`cal-osoba-${m.id}`}
            className={`filtr !pl-1.5 !pr-3.5 ${
              osoby.includes(m.id) ? "bg-ink text-white border-ink" : "bg-white text-sec border-line-strong hover:bg-inputbg"}`}>
            <OsobaKolecko id={m.id} name={m.name} url={m.photo_url} size={24} />
            {m.id === user?.id ? "Já" : (m.name || "").split(" ")[0]}
          </button>
        ))}
        <span className={`ml-auto flex items-center gap-3 ${kanban ? "hidden" : ""}`}>
          {LEGENDA.map(([label, c]) => (
            <span key={label} className="flex items-center gap-1.5 text-[11.5px] text-muted">
              <span className="h-2 w-2 rounded-full" style={{ background: c }} />{label}
            </span>
          ))}
        </span>
      </div>

      {/* po termínu — nad kalendářem, ať je vidět hned; karta s tečkou, nikdy plný červený pás */}
      {kanban && <KanbanSloupce k={k} onOtevri={setDetailId} />}

      {!kanban && poTerminu.length > 0 && (
        <div className="card mb-3 px-4 py-3 flex flex-wrap items-center gap-x-2.5 gap-y-1" data-testid="cal-po-terminu">
          <span className="h-2 w-2 rounded-full bg-danger shrink-0" />
          <span className="text-[12.5px] text-ink font-600 min-w-0 flex-1">
            {poTerminu.length} {poTerminu.length === 1 ? "událost" : poTerminu.length < 5 ? "události" : "událostí"} po termínu
            {nejstarsi && <span className="text-muted font-500"> — nejstarší z {new Date(nejstarsi.date_end).toLocaleDateString("cs-CZ", { day: "numeric", month: "numeric" })} ({nejstarsi.nazev})</span>}
          </span>
          <button onClick={prepniPoTerminu} aria-pressed={jenPoTerminu}
            className={`tap md:min-h-0 ml-auto pill h-8 px-3 text-[12px] font-700 inline-flex items-center gap-1.5 transition-colors ${
              jenPoTerminu ? "bg-ink text-white" : "text-accent hover:text-accent-hover"}`} data-testid="cal-po-terminu-vse">
            {jenPoTerminu ? <><X size={13} strokeWidth={2.6} /> Zavřít seznam</> : "Zobrazit vše →"}
          </button>
        </div>
      )}

      {kanban ? null : view === "SEZNAM" ? (
        <div className="card overflow-hidden" data-testid="cal-seznam">
          {(() => {
            const zdroj = jenPoTerminu ? poTerminu : visible;
            const items = [...zdroj].sort((x, y) => new Date(x.date_start) - new Date(y.date_start));
            if (!items.length) return <EmptyState icon="CalendarDays" title="Žádné aktivity"
              description={jenPoTerminu ? "Nic není po termínu." : "V tomto období není nic naplánováno."} />;
            let posledni = "";
            return items.map((a) => {
              const den = new Date(a.date_start).toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" });
              const hlavicka = den !== posledni; posledni = den;
              return (
                <React.Fragment key={a.id}>
                  {hlavicka && <div className="label-up text-muted px-4 pt-3 pb-1">{den}</div>}
                  <ActivityItem a={a} onToggle={toggle} onOpen={(x) => setDetailId(x.id)}
                    canEdit={canEditAktivita(user, a.oblast)} />
                </React.Fragment>
              );
            });
          })()}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          <div className="card p-3">
            <div className="grid grid-cols-7 mb-1">
              {DOW.map((d) => <div key={d} className="text-center label-up text-muted py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, i) => {
                const inMonth = view === "TYDEN" || d.getMonth() === cursor.getMonth();
                const items = byDay(d).sort((x, y) => new Date(x.date_start) - new Date(y.date_start));
                const isSel = sameDay(d, selected);
                const isToday = sameDay(d, new Date());
                const max = view === "TYDEN" ? 8 : 2;
                return (
                  <button key={i} onClick={() => setSelected(new Date(d))} data-testid={`cal-day-${d.getDate()}-${d.getMonth()}`}
                    className={`${view === "TYDEN" ? "min-h-[300px]" : "min-h-[68px] md:min-h-[84px]"} rounded-icon border p-1.5 text-left transition-colors align-top ${
                      isSel ? "border-accent bg-accent/5" : "border-line-softer hover:bg-inputbg"
                    } ${inMonth ? "" : "opacity-40"}`}>
                    <div className={`amount text-[12px] ${isToday ? "text-accent font-700" : "text-sec"}`}>
                      {isToday && <span className="label-up mr-1 text-accent">Dnes</span>}{d.getDate()}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {items.slice(0, max).map((a) => <EventBlok key={a.id} a={a} plny={view === "TYDEN"} />)}
                      {items.length > max && <div className="text-[9px] text-muted">+{items.length - max} další</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* denní náhled — klik na den v měsíci i týdnu */}
          <div className="card overflow-hidden self-start">
            <div className="px-4 py-3 border-b border-line-softer flex items-center justify-between">
              <h3 className="text-[13.5px] font-700 text-ink">
                {selected.getDate()}. {MONTHS[selected.getMonth()].toLowerCase()}
              </h3>
              <button onClick={() => setNewAct(true)} className="text-[12px] font-600 text-accent hover:text-accent-hover flex items-center gap-1">
                <Plus size={14} /> Přidat
              </button>
            </div>
            {dayActs.length === 0 ? (
              <EmptyState icon="CalendarDays" title="Žádné aktivity" description="V tento den nemáš naplánováno nic." />
            ) : (<>
              {/* Nahoře co zbývá udělat, hotové pod čarou jako historie dne. */}
              {dayActs.filter((a) => a.stav !== "HOTOVA").map((a) => (
                <ActivityItem key={a.id} a={a} onToggle={toggle} onOpen={(x) => setDetailId(x.id)} canEdit={canEditAktivita(user, a.oblast)} />
              ))}
              {dayActs.every((a) => a.stav === "HOTOVA") && (
                <div className="px-4 py-3 t-meta text-success" data-testid="cal-den-vse-hotovo">Vše na tento den je hotové.</div>
              )}
              {dayActs.some((a) => a.stav === "HOTOVA") && (
                <div className="bg-[#fbfaf8] border-t border-line-softer" data-testid="cal-den-hotove">
                  <div className="px-4 pt-3 pb-1 label-up text-muted">Hotovo · {dayActs.filter((a) => a.stav === "HOTOVA").length}</div>
                  <div className="opacity-75">
                    {dayActs.filter((a) => a.stav === "HOTOVA").map((a) => (
                      <ActivityItem key={a.id} a={a} onToggle={toggle} onOpen={(x) => setDetailId(x.id)} canEdit={canEditAktivita(user, a.oblast)} />
                    ))}
                  </div>
                </div>
              )}
            </>)}
          </div>
        </div>
      )}

      {newAct && <NovaAktivita defaultDate={kanban ? null : selected} defaultTyp={kanban ? "Úkol" : null}
        onClose={() => setNewAct(false)} onSaved={nacist} />}
      {detailId && (
        <AktivitaDetail id={detailId} onClose={() => setDetailId(null)}
          onEdit={(a) => { setDetailId(null); setEditAct(a); }}
          onChanged={nacist} />
      )}
      {editAct && (
        <NovaAktivita aktivita={editAct} onClose={() => setEditAct(null)}
          onSaved={() => { setEditAct(null); nacist(); }} />
      )}
    </div>
  );
}
